import { ColorScheme } from '@shared/services/color-scheme-manager';
import { createNilReducer, createReducer, select } from '../core';
import { RootState } from '../store';

export type SettingsState = {
  locale?: string;
  theme?: ColorScheme;
  skipInteractions: Partial<Record<SkipInteraction, boolean>>;
};

export enum SkipInteraction {
  /**
   * Introductory steps of “We will now record your face + voice to confirm your identity.”
   */
  BioIntro = 'BIO_INTRO',

  /**
   * Pages that warn you we're about to request access to your camera.
   */
  CameraConsentPage = 'CAMERA_CONSENT_PAGE',

  /**
   * Pages that warn you we're about to request access to your microphone.
   */
  MicrophoneConsentPage = 'MICROPHONE_CONSENT_PAGE'
}

export function newSettingsState(): SettingsState {
  return {
    locale: undefined,
    theme: undefined,
    skipInteractions: {},
  };
}

// Reducers

export const loadSettingsSlice = createReducer((state, payload: SettingsState) => {
  state.settings = {
    ...newSettingsState(),
    ...payload,
  }
});

export const setLocale = createReducer((state, payload: { locale: string }) => {
  state.settings.locale = payload.locale;
});

export const setTheme = createReducer((state, payload: { theme: ColorScheme }) => {
  state.settings.theme = payload.theme;
});

export const clearTheme = createNilReducer((state) => {
  state.settings.theme = undefined;
});

export const setSkipInteraction = createReducer((state, payload: { interaction: SkipInteraction, skip: boolean }) => {
  const { interaction, skip } = payload;
  if (skip) {
    state.settings.skipInteractions[interaction] = true;
  } else {
    delete state.settings.skipInteractions[interaction];
  }
});

// Selectors

export const selectLocale = () => (state: RootState) => {
  return state.settings.locale;
};

export const selectTheme = () => (state: RootState) => {
  return state.settings.theme;
};

export const selectSkipInteractions = () => (state: RootState) => {
  return state.settings.skipInteractions;
};

export const shouldSkip = (interaction: SkipInteraction) => (state: RootState) => {
  const skips = select(state, selectSkipInteractions());
  return Boolean(skips[interaction]);
}
